import {
  CommentsInput,
  CommentsInputHandle,
  CommentsInputParams,
} from "@jugl-web/domain-resources/tasks-comments/components/CommentsInput";
import { CommentActionInfo } from "@jugl-web/domain-resources/tasks-comments/components/CommentActionInfo";
import {
  MAX_FILE_SIZE_MB,
  MAX_FILES_COUNT,
} from "@jugl-web/domain-resources/tasks-comments/components/CommentsInput/consts";
import { sanitizeFileName } from "@jugl-web/domain-resources/tasks-comments/utils/sanitizeFileName";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { TicketCommentModel } from "@jugl-web/rest-api/tickets/models/TicketComment";
import { TicketDetailsModel } from "@jugl-web/rest-api/tickets/models/TicketDetails";
import {
  isFileSizeValid,
  useAppVariant,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { getTicketCommentText } from "../../utils/getTicketCommentText";
import { useTicketMentions } from "./hooks/useTicketMentions";

export interface TicketCommentInputProps {
  entityId: string;
  meId: string;
  ticket: TicketDetailsModel;
  editingComment?: TicketCommentModel | null;
  onEditingCancel?: () => void;
  onSuccess?: (
    comment: TicketCommentModel,
    action: "create" | "update"
  ) => void;
}

export const TicketCommentInput: FC<TicketCommentInputProps> = ({
  entityId,
  meId,
  ticket,
  editingComment,
  onEditingCancel,
  onSuccess,
}) => {
  const ticketId = ticket.id;
  const { ticketsApi } = useRestApiProvider();
  const { variant } = useAppVariant();
  const { t } = useTranslations();
  const { toast } = useToast();
  const inputRef = useRef<CommentsInputHandle>(null);

  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [createComment, { isLoading: isCreating }] =
    ticketsApi.useCreateTicketCommentMutation();
  const [updateComment, { isLoading: isUpdating }] =
    ticketsApi.useUpdateCommentMutation();

  const isEditMode = Boolean(editingComment);
  const isLoading = isCreating || isUpdating;

  const { mentions } = useTicketMentions({ entityId, meId, ticket });

  const handleAttachmentAdd = useCallback(
    (attachments: File[]) => {
      setPendingAttachments((prev) => {
        const validAttachments = attachments.filter((file) =>
          isFileSizeValid(file, MAX_FILE_SIZE_MB)
        );

        if (validAttachments.length !== attachments.length) {
          toast(
            t(
              {
                id: "feedback.too-large-file-megabytes",
                defaultMessage:
                  "The file is too large. The maximum size is {fileSize}MB",
              },
              { fileSize: MAX_FILE_SIZE_MB }
            ),
            { variant: "error" }
          );
        }

        const remainingSlots = MAX_FILES_COUNT - prev.length;

        if (validAttachments.length > remainingSlots) {
          toast(
            t(
              {
                id: "tasks-page.upload-attachments-count-alert",
                defaultMessage:
                  "You can upload up to {maxAttachments} {maxAttachments, plural, one {attachment} other {attachments}}",
              },
              { maxAttachments: MAX_FILES_COUNT }
            ),
            { variant: "error" }
          );
        }

        return [...prev, ...validAttachments.slice(0, remainingSlots)];
      });
    },
    [t, toast]
  );

  const handleAttachmentRemove = useCallback((index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit: CommentsInputParams["onSubmit"] = useCallback(
    async ({ body, attachments }) => {
      const files = attachments?.map(
        (attachment) =>
          new File([attachment], sanitizeFileName(attachment.name), {
            type: attachment.type,
          })
      );

      try {
        if (isEditMode && editingComment) {
          const updatedComment = await updateComment({
            entityId,
            ticketId,
            commentId: editingComment.id,
            data: { text: body, files },
          }).unwrap();

          inputRef.current?.clear();
          setPendingAttachments([]);
          onSuccess?.(
            {
              ...updatedComment,
              updated_at:
                updatedComment.updated_at !== updatedComment.inserted_at
                  ? updatedComment.updated_at
                  : new Date().toISOString(),
            },
            "update"
          );
          onEditingCancel?.();
        } else {
          const createdComment = await createComment({
            entityId,
            ticketId,
            data: { text: body, files },
          }).unwrap();

          inputRef.current?.clear();
          setPendingAttachments([]);
          onSuccess?.(createdComment, "create");
        }

        inputRef.current?.focus();
      } catch {
        // Error toast is handled globally by the API result pipeline
      }
    },
    [
      createComment,
      editingComment,
      entityId,
      isEditMode,
      onEditingCancel,
      onSuccess,
      ticketId,
      updateComment,
    ]
  );

  useEffect(() => {
    if (!editingComment) {
      return;
    }

    inputRef.current?.setValue({
      body: getTicketCommentText(editingComment.content),
    });
    setPendingAttachments([]);
    inputRef.current?.focus();
  }, [editingComment]);

  useEffect(() => {
    if (!isEditMode) {
      setPendingAttachments([]);
    }
  }, [isEditMode]);

  return (
    <div className="flex w-full flex-col gap-1">
      {isEditMode && editingComment && (
        <CommentActionInfo
          isCancelDisabled={isLoading}
          onCancel={() => {
            onEditingCancel?.();
            inputRef.current?.clear();
          }}
          attachments={editingComment.content.attachments ?? []}
          text={getTicketCommentText(editingComment.content)}
        />
      )}
      <CommentsInput
        key={editingComment?.id ?? "new"}
        variant={variant}
        ref={inputRef}
        onSubmit={handleSubmit}
        mentions={mentions}
        isLoading={isLoading}
        attachments={pendingAttachments}
        onAttachmentAdd={handleAttachmentAdd}
        onAttachmentRemove={handleAttachmentRemove}
        hideAttachmentButton={isEditMode}
        placeholder={t({
          id: "client-support-page.add-your-note",
          defaultMessage: "Add your note",
        })}
        hasEditedAttachments={
          isEditMode && !!editingComment?.content.attachments?.length
        }
      />
    </div>
  );
};
